import axios from 'axios';
import { appMessage as message } from './utils/appMessage';
import { STORAGE_KEY } from './contexts/StorageContext';

/** 기본 응답 대기 한도(30초) — 백엔드는 살아 있는데 응답만 안 오는 상태에서 화면이
 * 무한 로딩으로 멈추지 않게 한다. 정상적으로 오래 걸리는 요청은 LONG_TIMEOUT_MS를 쓴다. */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** 엑셀 업로드처럼 서버 처리 시간이 긴 동기 요청용(2분) — src/api/api.ts에서 개별 지정. */
export const LONG_TIMEOUT_MS = 120_000;

// 2026-07-24 사고: env 누락 시 폴백이 조용히 localhost를 가리켜 운영 번들이 로그인 불가가 됐다 — 폴백 없이 env 값만 쓴다.
// 모듈 로드 때 throw 하지 않는다 — ErrorBoundary 가 없는 앱이라 흰 화면이 되고, 빌드도 막지 못한다.
// 빌드는 package.json 의 prebuild(scripts/check-env.js)가, 런타임은 src/index.tsx 가 안내를 맡는다.
const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  // JWT는 HttpOnly 쿠키로 내려온다(2026-08-19, localStorage 저장 시 XSS로 탈취될 수 있던
  // 위험 제거) — 브라우저가 자동으로 쿠키를 실어 보내게 하려면 cross-origin 요청(로컬
  // :3000→:8080)에도 이 옵션이 필요하다. 운영은 nginx가 같은 오리진에서 서빙해 사실상
  // 필요 없지만 로컬 개발엔 필수.
  withCredentials: true,
});

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * 요청별 매장 고정(감사 card-controltower#828) — 주면 보내는 순간의 localStorage superStore 대신 이 값을 `X-Store-Code` 로 싣는다
     * (null = 헤더 없음 — ADMIN · VIEWER 계정 매장). 확인 창처럼 연 시점과 보내는 시점이 떨어진 요청이 그 사이 바뀐 매장으로 가지 않게.
     * 연 시점 값은 {@link currentStoreHeader} 로 잡는다.
     */
    storeOverride?: string | null;
  }
}

/** 지금 요청에 실릴 `X-Store-Code`(SUPER 가 고른 매장, 없으면 null) — storeOverride 로 고정할 값을 잡을 때. */
export const currentStoreHeader = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY.superStore);
  } catch {
    return null;
  }
};

http.interceptors.request.use((config) => {
  // 요청별로 매장을 고정했으면 그 값(감사 #828) — 비었으면 헤더를 싣지 않는다.
  if (config.storeOverride !== undefined) {
    if (config.storeOverride) config.headers['X-Store-Code'] = config.storeOverride;
    return config;
  }
  // SUPER 가 대상 매장을 골랐으면 그 매장으로 조회·편집하도록 헤더로 알린다.
  // (매장 ADMIN 계정에는 이 값이 없어 헤더도 붙지 않고, 백엔드도 SUPER 에게만 적용한다.)
  const superStore = localStorage.getItem(STORAGE_KEY.superStore);
  if (superStore) config.headers['X-Store-Code'] = superStore;
  return config;
});

/** 409 사유를 서버가 안 줬을 때의 문구. */
export const CONFLICT_FALLBACK_REASON = '지금 상태에서는 진행할 수 없습니다 — 잠시 뒤 다시 시도해 주세요';

/**
 * 409(상태 위반 — 반영 막힘)의 사유. 아니면 null.
 *
 * 백엔드 PR #82(감사 card-controltower#690 · #700): ③ 스토어 반영(`/sync-naver`) · 가격 롤백(`/rollback-price`)은 PriceHub 확정 대기
 * 목록이 1,000건에서 잘리면 시작 단계에서 **409 + `{ message }`** 로 막힌다(fail-closed). 잡 락(다른 반영 · 조회 진행 중)도 409 다.
 * 403 은 권한 문제 전용이라(아래 인터셉터가 "권한 없음" 토스트) 막힘을 403 으로 받지 않는다 — 두 경우를 섞지 않게 409 는 인터셉터가
 * 아무것도 띄우지 않고, 부르는 쪽이 `blockedText` 로 "반영 막힘 — <사유>" 를 띄운다(useJobRunner 의 conflictLabel).
 */
export function conflictReason(error: unknown): string | null {
  const res = (error as { response?: { status?: number; data?: { message?: unknown } } } | null)?.response;
  if (res?.status !== 409) return null;
  const m = res.data?.message;
  return typeof m === 'string' && m.trim() !== '' ? m.trim() : CONFLICT_FALLBACK_REASON;
}

/** "반영 막힘 — <사유>" — 409 를 확인 창 · 토스트에 보일 때 같은 틀. */
export const blockedText = (reason: string, label = '반영 막힘'): string => `${label} — ${reason}`;

// 401(인증 실패: 토큰 없음/만료/위조) → 세션 초기화 후 로그인으로 복귀.
// 403(권한 부족: VIEWER가 ADMIN 작업 시도 등)은 로그아웃하지 않고 안내만 한다.
// 409(반영 막힘 · 상태 위반)는 여기서 띄우지 않는다 — 사유를 아는 부르는 쪽이 띄운다(conflictReason, 감사 #700).
// role 유무 체크로 동시 다발 요청 실패 시 메시지 중복 방지 — 토큰은 이제 HttpOnly 쿠키라
// JS에서 못 읽으므로, 계속 localStorage에 남는 비민감 값(role)을 대신 쓴다.
http.interceptors.response.use(
  (res) => res,
  (error) => {
    const status         = error.response?.status;
    const url: string    = error.config?.url || '';
    const isAuthEndpoint = url.includes('/api/auth/');

    if (status === 401 && !isAuthEndpoint && localStorage.getItem(STORAGE_KEY.role)) {
      message.warning('세션이 만료되었습니다. 다시 로그인해 주세요.');
      window.dispatchEvent(new Event('auth:expired'));
    } else if (status === 403 && !isAuthEndpoint) {
      message.warning('이 작업을 수행할 권한이 없습니다.');
    }
    return Promise.reject(error);
  },
);

export default http;